import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useAddEmail } from '../../hooks/apolloHooks/useAddEmail';
import { useAddPhoneNumber } from '../../hooks/apolloHooks/useAddPhoneNumber';
import { useDeletePhone } from '../../hooks/apolloHooks/useDeletePhone';
import { useResendEmailVerificationCode } from '../../hooks/apolloHooks/useResendEmailVerificationCode';
import { useResendPhoneVerificationCode } from '../../hooks/apolloHooks/useResendPhoneVerificationCode';
import { useUpdateEmail } from '../../hooks/apolloHooks/useUpdateEmail';
import { useUpdatePhone } from '../../hooks/apolloHooks/useUpdatePhone';
import { useVerifyEmail } from '../../hooks/apolloHooks/useVerifyEmail';
import { useVerifyPhone } from '../../hooks/apolloHooks/useVerifyPhone';
import { ContactDataResponse } from '../../integrations/allkort/graphql/interface/Customer';
import { validateEmail, validatePhone } from '../../utils/contactValidationSchemas';
import { EMAIL_TYPES } from '../../utils/filterEmailsByType';
import { PHONE_TYPE_IDS } from '../../utils/filterPhonesByType';
import SetMainEmail from '../elements/updateaccountinformation/SetMainEmail';
import SetMainPhone from '../elements/updateaccountinformation/SetMainPhone';
import { EmailUnreadIcon, MobileIcon, SimpleCheckmarkIcon, SuccessIcon } from '../svgs/ExpressonSVGs';

type UpdateAccountInformationProps = {
    customerContactData?: ContactDataResponse
    customerContactLoading: boolean
    customerContactError: Error | null | undefined
    accountId: number | undefined
}
const UpdateAccountInformation: React.FC<UpdateAccountInformationProps> = ({ customerContactData, accountId }) => {
    const dialogRef = useRef<HTMLDialogElement>(null)
    const hasInitialized = useRef(false)
    const [emailValue, setEmailValue] = useState('')
    const [emailError, setEmailError] = useState<string | undefined>(undefined)
    const [isEmailValid, setIsEmailValid] = useState(false)
    const [emailVerificationSent, setEmailVerificationSent] = useState(false)
    const [emailStepCompleted, setEmailStepCompleted] = useState(false)
    const [phoneStepCompleted, setPhoneStepCompleted] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [codeValue, setCodeValue] = useState('')
    const [addedEmailId, setAddedEmailId] = useState<number | null>(null)
    const [clickCount, setClickCount] = useState(0)
    const [isInitializing, setIsInitializing] = useState(true)
    const [emailVerifiedSinceStart, setEmailVerifiedSinceStart] = useState(false)
    const [storedUnverifiedEmail, setStoredUnverifiedEmail] = useState<string | null>(null)
    const [isEditingEmail, setIsEditingEmail] = useState(false)

    // Phone step state
    const [phoneValue, setPhoneValue] = useState('')
    const [phoneError, setPhoneError] = useState<string | undefined>(undefined)
    const [isPhoneValid, setIsPhoneValid] = useState(false)
    const [phoneVerificationSent, setPhoneVerificationSent] = useState(false)
    const [phoneCodeValue, setPhoneCodeValue] = useState('')
    const [addedPhoneId, setAddedPhoneId] = useState<number | null>(null)
    const [phoneSubmitError, setPhoneSubmitError] = useState<string | null>(null)
    const [storedUnverifiedPhone, setStoredUnverifiedPhone] = useState<string | null>(null)
    const [isEditingPhone, setIsEditingPhone] = useState(false)

    // Mutations functions handling Phone and Email operations
    const { addEmail, loading } = useAddEmail()
    const { addPhoneNumber, loading: phoneLoading } = useAddPhoneNumber()
    const { deletePhone } = useDeletePhone()
    const { updateEmail, loading: updateEmailLoading } = useUpdateEmail()
    const { updatePhone, loading: updatePhoneLoading } = useUpdatePhone()
    const { resendCode, loading: resendCodeLoading } = useResendEmailVerificationCode()
    const { resendCode: resendPhoneCode, loading: resendPhoneCodeLoading } = useResendPhoneVerificationCode()
    const { verifyEmail, loading: verifyLoading } = useVerifyEmail()
    const { verifyPhone, loading: verifyPhoneLoading } = useVerifyPhone()

    // Check existing MAIN emails and phones from contact data — runs only once on first load
    useEffect(() => {
        const emails = customerContactData?.currentCustomer?.emails
        const phones = customerContactData?.currentCustomer?.phoneNumbers
        // Wait until we actually have data before initializing
        if (emails === undefined || phones === undefined || hasInitialized.current) return

        hasInitialized.current = true

        // --- Phone check ---
        const verifiedPhoneMain = phones.find(p => p.type?.id === PHONE_TYPE_IDS.MAIN && p.verified)
        const unverifiedPhoneMain = phones.find(p => p.type?.id === PHONE_TYPE_IDS.MAIN && !p.verified)

        if (verifiedPhoneMain) {
            //console.log('[UpdateAccountInformation] Verified MAIN phone found, skipping phone step:', verifiedPhoneMain.number)
            setPhoneStepCompleted(true)
        } else if (unverifiedPhoneMain) {
            // Unverified phone exists — pre-fill the form so user can resend or update it
            //console.log('[UpdateAccountInformation] Unverified MAIN phone found, pre-filling:', unverifiedPhoneMain.number, 'id:', unverifiedPhoneMain.id)
            setPhoneValue(unverifiedPhoneMain.number)
            setIsPhoneValid(true)
            setAddedPhoneId(unverifiedPhoneMain.id)
            setStoredUnverifiedPhone(unverifiedPhoneMain.number)
        }

        // --- Email check ---
        const mainEmails = emails.filter(e => e.type.id === EMAIL_TYPES.MAIN_EMAIL)

        const verifiedEmailMain = mainEmails.find(e => e.verified)
        if (verifiedEmailMain) {
            // Already has a verified main email — skip the email step entirely
            //console.log('[UpdateAccountInformation] Verified MAIN email found, skipping email step:', verifiedEmailMain.address)
            setEmailVerifiedSinceStart(true)
            setEmailStepCompleted(true)
            setIsInitializing(false)
            return
        }

        const unverifiedEmailMain = mainEmails.find(e => !e.verified)
        if (unverifiedEmailMain) {
            // Unverified email exists — pre-fill the form so user can resend or update it
            //console.log('[UpdateAccountInformation] Unverified MAIN email found, pre-filling:', unverifiedEmailMain.address, 'id:', unverifiedEmailMain.id)
            setEmailValue(unverifiedEmailMain.address)
            setIsEmailValid(true)
            setAddedEmailId(unverifiedEmailMain.id)
            setStoredUnverifiedEmail(unverifiedEmailMain.address)
            setIsInitializing(false)
            return
        }

        setIsInitializing(false)
    }, [customerContactData])

    // ****** Email functions ********
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setEmailValue(value)
        const result = validateEmail(value)
        setIsEmailValid(result.valid)
        setEmailError(result.valid ? undefined : result.error)
    }

    const handleEmailVerify = async () => {
        // Re-validate here so DOM manipulation can't bypass the check
        const result = validateEmail(emailValue)
        if (!result.valid) return
        if (!accountId) return

        setSubmitError(null)
        try {
            if (addedEmailId) {
                if (emailValue === storedUnverifiedEmail) {
                    // Same email — just resend the verification code
                    await resendCode({ emailId: addedEmailId })
                } else {
                    // Email changed — updateEmails also triggers a new verification code
                    await updateEmail({
                        emailId: addedEmailId,
                        customerId: accountId,
                        address: emailValue,
                        emailType: 'MAIN',
                        sortOrder: 1,
                    })
                    setStoredUnverifiedEmail(emailValue)
                }
                setEmailVerificationSent(true)
                setIsEditingEmail(false)
                return
            }

            // No existing email — add new
            const added = await addEmail({
                address: emailValue,
                customerId: accountId,
                emailType: 'MAIN',
            })
            if (added) {
                setEmailVerificationSent(true)
                setAddedEmailId(added[0]?.id ?? null)
            }
        } catch (err: any) {
            console.error('[UpdateAccountInformation] handleEmailVerify failed:', err)
            setSubmitError('Något gick fel. Försök igen.')
        }
    }

    const handleEditEmail = () => {
        setEmailVerificationSent(false)
        setCodeValue('')
        setSubmitError(null)
        setIsEditingEmail(true)
    }

    const handleEmailCodeVerification = async () => {
        if (!codeValue || !addedEmailId) return
        setSubmitError(null)
        try {
            const verified = await verifyEmail({
                emailId: addedEmailId,
                verificationCode: codeValue,
            })
            console.log('[UpdateAccountInformation] Verify result:', verified)
            if (verified) {
                setEmailStepCompleted(true)
                setClickCount(0) // reset click count for demo purposes
            }
        } catch (err: any) {
            console.error('[UpdateAccountInformation] verifyEmail failed:', err)
            setSubmitError('Felaktig verifieringskod. Kontrollera koden och försök igen.')
            setCodeValue('')
        }
    }

    // ****** Phone functions ********
    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setPhoneValue(value)
        const result = validatePhone(value)
        setIsPhoneValid(result.valid)
        setPhoneError(result.valid ? undefined : result.error)
    }

    const handlePhoneVerify = async () => {
        const result = validatePhone(phoneValue)
        if (!result.valid) return
        if (!accountId) return
        setPhoneSubmitError(null)
        try {
            if (addedPhoneId) {
                if (phoneValue === storedUnverifiedPhone) {
                    // Same number — just resend the verification code
                    await resendPhoneCode({ phoneNumberId: addedPhoneId })
                } else {
                    // Number changed — updatePhoneNumbers also triggers a new SMS code
                    await updatePhone({
                        phoneId: addedPhoneId,
                        customerId: accountId,
                        description: 'Huvudtelefonnummer',
                        phoneNo: phoneValue,
                        phoneType: 'MAIN',
                    })
                    setStoredUnverifiedPhone(phoneValue)
                }
                setPhoneVerificationSent(true)
                setIsEditingPhone(false)
                return
            }

            // No existing phone — add new
            const added = await addPhoneNumber({
                customerId: accountId,
                phoneNo: phoneValue,
            })
            if (added) {
                setPhoneVerificationSent(true)
                setAddedPhoneId(added[0]?.id ?? null)
            }
        } catch (err: any) {
            console.error('[UpdateAccountInformation] handlePhoneVerify failed:', err)
            setPhoneSubmitError('Något gick fel. Försök igen.')
        }
    }

    const handleEditPhone = () => {
        setPhoneVerificationSent(false)
        setPhoneCodeValue('')
        setPhoneSubmitError(null)
        setIsEditingPhone(true)
    }

    const handlePhoneCodeVerification = async () => {
        if (!phoneCodeValue || !addedPhoneId) return
        setPhoneSubmitError(null)
        try {
            const verified = await verifyPhone({
                phoneNumberId: addedPhoneId,
                verificationCode: phoneCodeValue,
            })
            console.log('[UpdateAccountInformation] verifyPhone result:', verified)
            if (verified?.verified) {
                setPhoneStepCompleted(true)
            }
        } catch (err: any) {
            console.error('[UpdateAccountInformation] verifyPhone failed:', err)
            // C0026: phone is already verified — treat as success
            if (err?.message?.toLowerCase().includes('already verified')) {
                setPhoneStepCompleted(true)
                return
            }
            setPhoneSubmitError('Felaktig verifieringskod. Kontrollera koden och försök igen.')
            setPhoneCodeValue('')
        }
    }

    // Close button handle
    const handleSuccess = () => {
        dialogRef.current?.close()
    }

    // Prevent dialog from closing via cancel event (Escape key, Android back button, or click outside)
    useEffect(() => {
        const dialog = dialogRef.current
        if (!dialog) return

        // Prevent cancel event (fired by Escape key, Android back button, clicking backdrop)
        const handleCancel = (e: Event) => {
            e.preventDefault()
        }

        // Prevent Escape key as fallback
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault()
            }
        }

        dialog.addEventListener('cancel', handleCancel)
        dialog.addEventListener('keydown', handleKeyDown)

        return () => {
            dialog.removeEventListener('cancel', handleCancel)
            dialog.removeEventListener('keydown', handleKeyDown)
        }
    }, [])

    useEffect(() => {
        const timer = setTimeout(() => {
            dialogRef.current?.showModal()
        }, 100)
        return () => clearTimeout(timer)
    }, [])



    return (
        <div className="update-account-information_component">
            <dialog
                ref={dialogRef}
                className="update-account-information-dialog"
            >
                <div className="update-account-information-dialog__header">
                    <div className={classNames(
                        "progress-email",
                        { 'completed': emailStepCompleted }
                    )}>
                        <span>1</span>
                        <span className="svg-icon-wrapper round green">
                            <SimpleCheckmarkIcon width={18} height={18} />
                        </span>
                        E-post
                    </div>

                    <div className={classNames(
                        "progress-bar",
                        { 'email-completed': emailStepCompleted }
                    )}><span></span></div>

                    <div className={classNames(
                        "progress-phone",
                        {
                            'email-completed': emailStepCompleted,
                            'phone-completed': phoneStepCompleted
                        },
                    )}>
                        <span>2</span>
                        <span className="svg-icon-wrapper round green">
                            <SimpleCheckmarkIcon width={18} height={18} />
                        </span>
                        Telefon
                    </div>
                </div>

                <div className="update-account-information-dialog__content">
                    <div className="content-intro">
                        <span
                            className="svg-icon-wrapper round blue"
                            onClick={() => {
                                const next = clickCount + 1
                                setClickCount(next)
                                if (next >= 10) handleSuccess()
                            }}
                        >
                            {!emailStepCompleted ? (
                                <EmailUnreadIcon width={28} height={28} />
                            ) : !phoneStepCompleted ? (
                                <MobileIcon width={28} height={28} />
                            ) : (
                                <SuccessIcon className="pulse" width={28} height={28} />
                            )}
                        </span>
                        <h5 className="body-text">
                            {!emailStepCompleted ? (
                                'Uppdatera kontoinformation'
                            ) : !phoneStepCompleted ? (
                                'Verifiera telefonnummer'
                            ) : (
                                'Klart!'
                            )}

                        </h5>
                        {!emailStepCompleted ? (
                            <p>
                                Hjälp oss hålla kontakten. Vi saknar {!emailStepCompleted && !phoneStepCompleted ? (<strong>e-postadress och telefonnummer</strong>) : <strong>din e-postadress</strong>} för ditt konto. Uppdatera dem gärna nu - det krävs för att du ska kunna gå vidare och säkerställer att du inte missar viktig information.
                            </p>
                        ) : !phoneStepCompleted ? (
                            <>
                                {emailVerifiedSinceStart && (
                                    <p>
                                        Hjälp oss hålla kontakten. Vi saknar <strong>telefonnummer</strong> för ditt konto. Uppdatera det gärna nu - det krävs för att du ska kunna gå vidare och säkerställer att du inte missar viktig information.
                                    </p>
                                )}
                                <p>
                                    Ange ditt mobilnummer för att slutföra verifieringen. Vi skickar en engångskod via SMS för att bekräfta att numret är ditt.
                                </p>
                            </>
                        ) : (
                            <p>
                                Grattis! Du har uppdaterat all kontoinformation.
                            </p>
                        )}
                    </div>

                    {isInitializing ? null : !emailStepCompleted ? (
                        <SetMainEmail
                            emailValue={emailValue}
                            emailError={emailError}
                            isEmailValid={isEmailValid}
                            loading={loading || updateEmailLoading || resendCodeLoading}
                            handleEmailChange={handleEmailChange}
                            handleEmailVerify={handleEmailVerify}
                            emailVerificationSent={emailVerificationSent}
                            isEmailInputDisabled={emailVerificationSent || (!!storedUnverifiedEmail && !isEditingEmail)}
                            addedEmailId={addedEmailId}
                            codeValue={codeValue}
                            setCodeValue={setCodeValue}
                            verifyLoading={verifyLoading}
                            handleCodeVerification={handleEmailCodeVerification}
                            onEditEmail={handleEditEmail}
                            submitError={submitError}
                        />
                    ) : !phoneStepCompleted ? (
                        <SetMainPhone
                            phoneValue={phoneValue}
                            phoneError={phoneError}
                            isPhoneValid={isPhoneValid}
                            loading={phoneLoading || updatePhoneLoading || resendPhoneCodeLoading}
                            handlePhoneChange={handlePhoneChange}
                            handlePhoneVerify={handlePhoneVerify}
                            phoneVerificationSent={phoneVerificationSent}
                            isPhoneInputDisabled={phoneVerificationSent || (!!storedUnverifiedPhone && !isEditingPhone)}
                            addedPhoneId={addedPhoneId}
                            codeValue={phoneCodeValue}
                            setCodeValue={setPhoneCodeValue}
                            verifyLoading={verifyPhoneLoading}
                            handleCodeVerification={handlePhoneCodeVerification}
                            submitError={phoneSubmitError}
                            onEditPhone={handleEditPhone}
                        />
                    ) : (
                        null // All steps completed, show nothing in the content area except the intro text and the close button
                    )}

                </div>

                {/* Close button, shown when all steps are completed */}
                {emailStepCompleted && phoneStepCompleted && (
                    <button
                        onClick={handleSuccess}
                        className="Button close-button dashboard-button">
                        Stäng
                    </button>
                )}

            </dialog>
        </div>
    )
}

export default UpdateAccountInformation;
